import React from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { STRING } from "@constants";
import {
  UnitDescriptionUpdate,
  UnitsDescriptionFormValues,
  UnitsDescriptionRead,
} from "@types";
import { UseManageSizesFormProps } from "./types";
import {
  CreateFeatureInitialValues,
  fields,
  SIZE_FORM_SECTIONS,
} from "./constants";
import { useCreateUnitDescriptions, useUpdateUnitDescriptions } from "@api";
import { useManageForm } from "@hooks";

export function UnitDescriptionForm({
  editValues,
  onClose,
}: UseManageSizesFormProps) {
  const { triggerCreateUnitDescription } = useCreateUnitDescriptions();
  const { triggerUpdateUnitDescription } = useUpdateUnitDescriptions();
  const { sendForm } = useManageForm<
    UnitDescriptionUpdate,
    UnitsDescriptionRead
  >({
    createFn: (data) => triggerCreateUnitDescription(data),
    updateFn: (id, data) => triggerUpdateUnitDescription({ id, payload: data }),
    successMessage: {
      create: "Unit Description successfully created",
      update: "Unit Description successfully updated",
    },
  });

  const initialValues: UnitsDescriptionFormValues = editValues
    ? { ...editValues }
    : CreateFeatureInitialValues;

  const handleCancel = (dirty: boolean, resetForm: () => void) => {
    if (!dirty) return onClose();
    confirmDialog({
      message: STRING.UNIT_DESCRIPTION_FORM.CONFIRM_DIALOG_MESSAGE,
      header: STRING.UNIT_DESCRIPTION_FORM.CONFIRM_DIALOG_HEADER,
      icon: STRING.UNIT_DESCRIPTION_FORM.CONFIRM_DIALOG_ICON,
      acceptClassName: STRING.UNIT_DESCRIPTION_FORM.CONFIRM_DIALOG_ACCEPT_CLASS,
      accept: () => {
        resetForm();
        onClose();
      },
    });
  };
  const handleSubmit = async (
    values: UnitDescriptionUpdate,
    formikHelpers: FormikHelpers<UnitDescriptionUpdate>,
  ) => {
    await sendForm(values);
    formikHelpers.resetForm();
    onClose();
  };

  return (
    <>
      <ConfirmDialog />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        {({ isSubmitting, dirty, resetForm, values, setFieldValue }) => (
          <Form className="flex flex-column h-full">
            <div className="flex-1 overflow-auto px-3 bg-surface-50">
              {SIZE_FORM_SECTIONS.map(({ title, keys }) => {
                const sectionFields = fields.filter((f) =>
                  keys.includes(f.name),
                );
                return (
                  <section key={title} className="mb-5">
                    <h3 className="text-lg font-semibold mb-4">{title}</h3>
                    <div className="grid formgrid p-fluid m-0">
                      {sectionFields.map((field) => (
                        <div key={field.name} className="col-12 mb-5">
                          <label
                            htmlFor={field.name}
                            className="text-sm text-surface-500 mb-2 font-medium block"
                          >
                            {field.label}
                          </label>
                          <Dropdown
                            id={field.name}
                            name={field.name}
                            value={
                              values[
                                field.name as keyof UnitsDescriptionFormValues
                              ]
                            }
                            options={field.options || []}
                            showClear
                            onChange={(e) =>
                              setFieldValue(field.name, e.value ?? null)
                            }
                            placeholder={`Select ${field.label}`}
                            className="w-full"
                          />
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="surface-0 border-top-1 mt-4 surface-border py-4 px-4 flex justify-content-end gap-3">
              <Button
                type="button"
                label={STRING.UNIT_DESCRIPTION_FORM.CANCEL_BUTTON}
                icon={STRING.UNIT_DESCRIPTION_FORM.CANCEL_ICON}
                className="p-button-outlined p-button-secondary"
                onClick={() => handleCancel(dirty, resetForm)}
              />
              <Button
                type="submit"
                label={
                  editValues
                    ? STRING.UNIT_DESCRIPTION_FORM.UPDATE_BUTTON
                    : STRING.UNIT_DESCRIPTION_FORM.CREATE_BUTTON
                }
                icon={
                  editValues
                    ? STRING.UNIT_DESCRIPTION_FORM.UPDATE_ICON
                    : STRING.UNIT_DESCRIPTION_FORM.CREATE_ICON
                }
                className="p-button-primary"
                loading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
